import React, { useEffect, useState } from 'react';
import { X, FileText, Download, RotateCcw, CheckCircle } from 'lucide-react';
import {
  getTreatmentInvoiceData,
  runOcrTreatmentInvoice,
  saveTreatmentInvoice,
  verifyTreatmentInvoice,
} from '../../api/loan-action-creator';
import './invoiceUpload.css';

const emptyInvoice = {
  customer_name: '',
  treatment_name: '',
  invoice_number: '',
  billing_date: '',
  total_amount: '',
};

const normalizeInputDate = (dateString) => {
  if (!dateString) return '';
  const text = String(dateString).trim();
  const match = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (match) {
    return `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  return text;
};

const InvoiceUploadModal = ({ open, onClose, loanId, onSaved }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [ocrData, setOcrData] = useState(emptyInvoice);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [ocrRan, setOcrRan] = useState(false);
  const [ocrSuccess, setOcrSuccess] = useState(false);
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [mode, setMode] = useState('add');
  const [verifyData, setVerifyData] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [finalVerified, setFinalVerified] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  const loadInvoiceState = () => {
    if (!loanId) return;
    getTreatmentInvoiceData(
      loanId,
      (res) => {
        const data = res?.data || {};
        if (data.invoiceUrl) {
          setInvoiceUrl(data.invoiceUrl);
          setMode('show');
          setFinalVerified(data.verificationStatus === 'Verified');
          fetchVerifyData();
        } else {
          setMode('add');
          setInvoiceUrl('');
          setFinalVerified(false);
        }
      },
      () => {
        setMode('add');
        setInvoiceUrl('');
      }
    );
  };

  const fetchVerifyData = () => {
    if (!loanId) return;
    setVerifyLoading(true);
    verifyTreatmentInvoice(
      loanId,
      (res) => {
        setVerifyData(res.data);
        if (res.data?.overall_decision === 'verified') {
          setFinalVerified(true);
        }
        setVerifyLoading(false);
      },
      () => setVerifyLoading(false)
    );
  };

  useEffect(() => {
    if (!open || !loanId) return;
    setFile(null);
    setError('');
    setOcrData(emptyInvoice);
    setOcrRan(false);
    setOcrSuccess(false);
    setStatusMessage('');
    loadInvoiceState();
  }, [open, loanId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowed.includes(selected.type)) {
      setError('Only PDF or JPG/PNG files are allowed');
      return;
    }
    setError('');
    setFile(selected);
    setOcrRan(false);
    setOcrSuccess(false);
    setOcrData(emptyInvoice);
  };

  const handleRunOcr = () => {
    if (!file) {
      setStatusMessage('Please upload an invoice first');
      return;
    }

    setOcrLoading(true);
    setStatusMessage('');
    runOcrTreatmentInvoice(
      file,
      loanId,
      (ocrResult) => {
        const formatted = {
          ...(ocrResult.data || emptyInvoice),
          billing_date: normalizeInputDate(ocrResult.data?.billing_date),
        };
        setOcrData(formatted);
        setOcrRan(true);
        setOcrSuccess(true);
        setOcrLoading(false);
        setStatusMessage('OCR completed. Saving invoice...');
        handleSave(formatted, { auto: true });
      },
      () => {
        setOcrRan(true);
        setOcrSuccess(false);
        setOcrData(emptyInvoice);
        setOcrLoading(false);
        setStatusMessage('OCR failed. Please enter details manually.');
      }
    );
  };

  const handleSave = (overrideData = null, options = {}) => {
    const invoiceData = overrideData || ocrData;
    if (!file) {
      setStatusMessage('Please upload an invoice file');
      return;
    }

    setSavingInvoice(true);
    if (!options.auto) setStatusMessage('');
    saveTreatmentInvoice(
      file,
      loanId,
      invoiceData,
      (res) => {
        setSavingInvoice(false);
        setOcrSuccess(true);
        setInvoiceUrl(res?.data?.invoiceUrl || '');
        setMode('show');
        setStatusMessage('Invoice saved successfully');
        if (res.verification) setVerifyData(res.verification);
        if (res.verification?.overall_decision === 'verified') setFinalVerified(true);
        if (onSaved) onSaved();
      },
      (err) => {
        setSavingInvoice(false);
        setOcrSuccess(false);
        setStatusMessage(err.message || 'Failed to save invoice');
      }
    );
  };

  if (!open) return null;

  const lowerUrl = (invoiceUrl || '').toLowerCase();
  const isPdf =
    lowerUrl.includes('type=pdf') || lowerUrl.includes('.pdf') || lowerUrl.includes('downloadpdf');
  const isImage =
    lowerUrl.includes('.jpg') ||
    lowerUrl.includes('.jpeg') ||
    lowerUrl.includes('.png') ||
    lowerUrl.includes('.webp');

  return (
    <div className="inv-modal-backdrop">
      <div className="inv-modal" role="dialog" aria-labelledby="inv-modal-title">
        <header className="inv-modal-head">
          <h2 id="inv-modal-title">Invoice upload</h2>
          <button type="button" className="inv-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="inv-modal-body">
          <div className="inv-card">
            <div className="inv-card-title">
              <span className="inv-card-icon">
                <FileText size={16} />
              </span>
              Treatment invoice
            </div>

            {statusMessage && <p className="inv-status">{statusMessage}</p>}
            {error && <p className="inv-error">{error}</p>}

            {mode === 'show' && invoiceUrl && (
              <div className="inv-show-block">
                <div className="inv-actions">
                  <a className="inv-btn primary" href={invoiceUrl} target="_blank" rel="noopener noreferrer">
                    <Download size={15} />
                    Download invoice
                  </a>
                  {!finalVerified && (
                    <button
                      type="button"
                      className="inv-btn outline"
                      onClick={() => {
                        setMode('add');
                        setFile(null);
                        setOcrData(emptyInvoice);
                        setOcrRan(false);
                      }}
                    >
                      <RotateCcw size={15} />
                      Re-upload
                    </button>
                  )}
                </div>

                {finalVerified && (
                  <span className="inv-verified">
                    <CheckCircle size={14} />
                    Verified
                  </span>
                )}

                {isPdf && (
                  <iframe src={invoiceUrl} title="Invoice preview" className="inv-preview-frame" />
                )}
                {isImage && !isPdf && (
                  <img src={invoiceUrl} alt="Invoice preview" className="inv-preview-image" />
                )}

                {verifyLoading && <p className="inv-muted">Verifying invoice...</p>}

                {verifyData && (
                  <div className="inv-verify-block">
                    <h4>Verification result</h4>
                    <p className={verifyData.overall_decision === 'verified' ? 'pass' : 'fail'}>
                      Overall decision: {String(verifyData.overall_decision || '').toUpperCase()}
                    </p>
                    {verifyData.checks &&
                      Object.entries(verifyData.checks).map(([key, value]) => (
                        <div key={key} className="inv-verify-item">
                          <strong>{key.replace(/_/g, ' ')}</strong>
                          <span>{value.decision}</span>
                          {value.score != null && <small>Score: {value.score}%</small>}
                          {value.description && <small>{value.description}</small>}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {mode !== 'show' && (
              <div className="inv-upload-block">
                <label className="inv-upload-box">
                  {file ? file.name : 'Upload invoice (PDF / image)'}
                  <input
                    type="file"
                    accept="application/pdf,image/jpeg,image/jpg,image/png"
                    hidden
                    onChange={handleFileChange}
                  />
                </label>

                <button
                  type="button"
                  className="inv-btn primary"
                  disabled={ocrLoading || !file}
                  onClick={handleRunOcr}
                >
                  {ocrLoading ? 'Extracting…' : 'Run OCR'}
                </button>

                {ocrRan && (
                  <div className="inv-fields">
                    <input
                      value={ocrData.customer_name}
                      onChange={(e) => setOcrData({ ...ocrData, customer_name: e.target.value })}
                      placeholder="Customer name"
                    />
                    <input
                      value={ocrData.treatment_name}
                      onChange={(e) => setOcrData({ ...ocrData, treatment_name: e.target.value })}
                      placeholder="Treatment name"
                    />
                    <input
                      value={ocrData.invoice_number}
                      onChange={(e) => setOcrData({ ...ocrData, invoice_number: e.target.value })}
                      placeholder="Invoice number"
                    />
                    <input
                      type="date"
                      value={normalizeInputDate(ocrData.billing_date)}
                      onChange={(e) => setOcrData({ ...ocrData, billing_date: e.target.value })}
                    />
                    <input
                      value={ocrData.total_amount}
                      onChange={(e) => setOcrData({ ...ocrData, total_amount: e.target.value })}
                      placeholder="Total amount"
                    />
                    <button
                      type="button"
                      className="inv-btn primary"
                      disabled={savingInvoice}
                      onClick={() => handleSave()}
                    >
                      {savingInvoice ? 'Saving…' : 'Save invoice'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceUploadModal;
